'use client';
import { useDesitnationCollection } from '@commons/DesitnationCollection/useDesitnationCollection';
import { useDestinationFilter } from '@commons/DestinationFilter/useDestinationFilter';
import { setDestinationContext } from '@commons/DestinationFilter/destinationContextStore';
import './_supertag-picker.scss';
import Heading from '@commons/Heading/Heading';
import { useSupertag } from '@commons/SupertagProvider/SupertagProvider';
import { Checkmark, SupertagCulture, SupertagFamily, SupertagNature } from '@icons';
import Dropdown from '@commons/Dropdown/Dropdown';
import CustomCheckbox from '@components/CustomCheckbox/CustomCheckbox';
import { useEffect, useState } from 'react';
import Button from '@commons/Button';
import HtmlToJsx from '@commons/HtmlToJsx/HtmlToJsx';
import BlurbTag from '@commons/Blurb/Blurb.Tag';
import useDictionary from '@modules/Dictionary/useDictionary';

export default function SupertagPicker() {
  const { dictionary } = useDictionary();
  const { supertags, categories } = useDesitnationCollection();
  const { setCategoryFilters } = useDestinationFilter();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [buttonText, setButtonText] = useState('MORE OPTIONS');

  useEffect(() => {
    const timer = setTimeout(() => {
      setButtonText(
        isExpanded
          ? dictionary?.supertagPicker?.lessOptions || 'LESS OPTIONS'
          : dictionary?.supertagPicker?.moreOptions || 'MORE OPTIONS'
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [isExpanded, dictionary]);

  // Terms carry no language-neutral key, so match both the Norwegian and English titles.
  const supertagMap = {
    natur: SupertagNature,
    nature: SupertagNature,
    kultur: SupertagCulture,
    culture: SupertagCulture,
    familie: SupertagFamily,
    family: SupertagFamily
  };

  const { supertag, setSupertag } = useSupertag();
  function handleSelectSupertag(id) {
    if (supertag && supertag.includes(id)) {
      setSupertag(supertag.filter((t) => t !== id));
    } else {
      setSupertag([...(supertag || []), id]);
    }
  }

  function handleToggleFilter(filterObj) {
    setSelectedFilters((prev) => {
      const exists = prev.find((f) => f.id === filterObj.id);
      if (exists) {
        return prev.filter((f) => f.id !== filterObj.id);
      }
      return [...prev, filterObj];
    });
  }

  useEffect(() => {
    setCategoryFilters([...selectedFilters]);
  }, [selectedFilters, setCategoryFilters]);

  // Mirror the visitor's expressed interests (supertag tiles + category
  // checkboxes) to the shared store so the chat assistant — mounted outside
  // this context — can read them at send time. Store resolved titles.
  useEffect(() => {
    const allSupertags = /** @type {any[]} */ (supertags ?? []);
    const selectedIds = /** @type {any[]} */ (supertag ?? []);
    setDestinationContext({
      supertags: allSupertags.filter((t) => selectedIds.includes(t.id)).map((t) => t.title),
      categories: selectedFilters.map((f) => f.title)
    });
  }, [supertag, supertags, selectedFilters]);

  // Filter categories to show the main 6 from the design if they exist in category tree
  // If not matching perfectly, we simply render all top-level categories from categoriesTree.
  return (
    <section className={`SupertagPicker Layout--subgrid ${isExpanded ? 'is-expanded' : ''}`}>
      <Heading level={2} className="SupertagPicker__title">
        {dictionary?.supertagPicker?.tailorPreferences || 'Tailor on your preferences'}
      </Heading>
      <Heading level={4} className="SupertagPicker__description">
        {dictionary?.supertagPicker?.tailorDescription ||
          'Tell us about your interests and preferences so we can tailor the inspiration for you'}
      </Heading>
      <div className="SupertagPicker__supertags">
        {supertags?.map((tag) => {
          const Icon = supertagMap[tag.title?.trim().toLowerCase()] || null;
          const isSelected = supertag && supertag.includes(tag.id);
          return (
            <div
              key={tag.id}
              className={`SupertagPicker__supertag ${isSelected ? 'SupertagPicker__supertag--selected' : ''}`}
              onClick={() => handleSelectSupertag(tag.id)}
            >
              <div className="SupertagPicker__supertag-left"></div>
              <div className="SupertagPicker__supertag-center">
                {Icon && <Icon />}
                <Heading level={4}>{tag.title}</Heading>
              </div>
              <div className="SupertagPicker__supertag-right">{isSelected && <Checkmark />}</div>
            </div>
          );
        })}
      </div>

      {isExpanded ? (
        <div className="SupertagPicker__expanded-wrapper--open">
          <div className="SupertagPicker__expanded">
            <div className="SupertagPicker__categories">
              {categories
                ?.filter((c) => c.parent === 0 && !c.dropdown)
                .map((parentCategory) => {
                  const children = categories.filter((c) => c.parent === parentCategory.id);

                  // Optionally, if you only want to render groups that actually have checkboxes:
                  if (children.length === 0) return null;

                  return (
                    <div key={parentCategory.id} className="SupertagPicker__category-group">
                      <Heading level={4} className="SupertagPicker__category-title">
                        <HtmlToJsx html={parentCategory.title} />
                      </Heading>
                      <ul className="SupertagPicker__checkbox-list">
                        {children.map((child) => (
                          <li key={child.id}>
                            <CustomCheckbox
                              checked={!!selectedFilters.find((f) => f.id === child.id)}
                              onChange={() => handleToggleFilter(child)}
                            >
                              <HtmlToJsx html={child.title} />
                            </CustomCheckbox>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
            </div>

            {categories?.some((c) => c.dropdown === true) && (
              <div className="SupertagPicker__dropdowns">
                {categories
                  .filter((c) => c.dropdown === true)
                  .map((tagGroup) => {
                    const groupId = tagGroup.id;
                    const groupOptions = categories.filter((c) => c.parent === groupId);

                    const longestOptionLabel = groupOptions.reduce((longest, current) => {
                      return current.title.length > longest.length ? current.title : longest;
                    }, tagGroup.title);

                    return (
                      <Dropdown
                        key={groupId}
                        className="SupertagPicker__dropdown-inner"
                        id={groupId}
                        label={<HtmlToJsx html={tagGroup.title} />}
                        longestOptionLabel={longestOptionLabel}
                      >
                        <ul className="SupertagPicker__checkbox-list">
                          {groupOptions.map((child) => (
                            <li key={child.id}>
                              <CustomCheckbox
                                checked={!!selectedFilters.find((f) => f.id === child.id)}
                                onChange={() => handleToggleFilter(child)}
                              >
                                <HtmlToJsx html={child.title} />
                              </CustomCheckbox>
                            </li>
                          ))}
                        </ul>
                      </Dropdown>
                    );
                  })}
              </div>
            )}

            <div className="SupertagPicker__footer">
              <div className="SupertagPicker__selected-row">
                <span className="SupertagPicker__selected-label">
                  {dictionary?.supertagPicker?.selected || 'SELECTED:'}
                </span>
                {selectedFilters.map((filter) => (
                  <BlurbTag key={filter.id} className="SupertagPicker__selected-tag">
                    <HtmlToJsx html={filter.title} />
                  </BlurbTag>
                ))}
                {selectedFilters.length > 0 && (
                  <button className="SupertagPicker__clear-all" onClick={() => setSelectedFilters([])}>
                    {dictionary?.supertagPicker?.clearAll || 'CLEAR ALL'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <Button priority="secondary" size="s" className="SupertagPicker__more" onClick={() => setIsExpanded(!isExpanded)}>
        {buttonText}
      </Button>
    </section>
  );
}
